import type { components } from '@/types/api/v1'
import { DeleteOutlined, EditFilled, PlusOutlined } from '@ant-design/icons'
import { GraviButton, Texto } from '@gravitate-js/excalibrr'
import { Card, Dropdown, Form, Popover, Select } from 'antd'
import { useState } from 'react'
import { AmIcon, PmIcon } from './ShiftIcons'
import './shiftTile.css'

type ShiftData = components['schemas']['ScheduleDay']

const convert24to12Hour = (hours24: number) => {
  const suffix = hours24 >= 12 ? 'PM' : 'AM'
  const hours = hours24 === 0 ? 12 : hours24 > 12 ? hours24 - 12 : hours24

  return {
    hours,
    suffix,
  }
}
function StartHourDisplay({ value }: { value: ShiftData['shift_start_hour_local'] }) {
  if (value === null || value === undefined) {
    return <Texto weight='bolder'>--:--</Texto>
  }
  const { hours, suffix } = convert24to12Hour(value)
  return (
    <div className='tile-row ml-3' style={{ gap: '2px' }}>
      <Texto weight='bolder'>{`${hours}:00`}</Texto>
      <Texto appearance='hint' style={{ alignSelf: 'flex-end' }} weight='600' category='label'>
        {suffix}
      </Texto>
    </div>
  )
}
const hourOptions = Array.from({ length: 24 }, (_, i) => {
  const { hours, suffix } = convert24to12Hour(i)
  return {
    value: i,
    label: `${hours}:00 ${suffix}`,
  }
})

type ShiftTileProps = {
  value?: ShiftData
  onChange?: (val: ShiftData) => void
}
export function ShiftTile({ value, onChange }: ShiftTileProps) {
  const editable = onChange !== undefined
  const [isOpen, setIsOpen] = useState(false)
  const [showEdit, setShowEdit] = useState(false)

  if (!value?.shift) {
    return (
      <div className='shift-tile no-shift'>
        {editable ? (
          <Dropdown
            menu={{
              items: [
                {
                  key: 'add-shift',
                  disabled: true,
                  icon: <PlusOutlined />,
                  label: (
                    <Texto appearance='hint' category='p2'>
                      Add To Slot
                    </Texto>
                  ),
                },
                {
                  key: 'am-shift',
                  icon: <AmIcon />,
                  label: <Texto category='p2'>AM Shift</Texto>,
                  onClick: () => onChange({ shift: 'am' }),
                },
                {
                  key: 'pm-shift',
                  icon: <PmIcon />,
                  label: <Texto category='p2'>PM Shift</Texto>,
                  onClick: () => onChange({ shift: 'pm' }),
                },
              ],
            }}
            trigger={['click']}
          >
            <GraviButton size='small' icon={<PlusOutlined />} />
          </Dropdown>
        ) : null}
      </div>
    )
  }
  const isAM = value.shift === 'am'
  return (
    <Popover
      trigger='click'
      open={isOpen}
      onOpenChange={(v) => (editable || !v ? setIsOpen(v) : undefined)}
      content={
        <Card
          title='Edit Shift'
          extra={<GraviButton size='small' error icon={<DeleteOutlined />} onClick={() => onChange?.({})} />}
        >
          <Form>
            <Form.Item label='Shift Type'>
              <Select
                value={value.shift}
                options={[
                  {
                    value: 'am',
                    label: (
                      <div className='tile-row'>
                        <AmIcon />
                        <Texto>AM Shift</Texto>
                      </div>
                    ),
                  },
                  {
                    value: 'pm',
                    label: (
                      <div className='tile-row'>
                        <PmIcon />
                        <Texto>PM Shift</Texto>
                      </div>
                    ),
                  },
                ]}
                onChange={(val) => onChange?.({ shift: val })}
              />
            </Form.Item>
            <Form.Item label='Start Hour'>
              <Select
                value={value.shift_start_hour_local}
                options={hourOptions}
                allowClear
                onChange={(v) => onChange?.({ shift: value.shift, shift_start_hour_local: v })}
              />
            </Form.Item>
          </Form>
        </Card>
      }
      styles={{ body: { padding: '0px' } }}
    >
      <GraviButton
        onMouseEnter={() => (editable ? setShowEdit(true) : undefined)}
        onMouseLeave={() => setShowEdit(false)}
        color={isAM ? 'yellow' : undefined}
        theme2={!isAM}
        appearance='filled'
        className={`shift-tile ${value.shift}`}
        style={{ cursor: editable ? 'pointer' : 'default' }}
      >
        <div className='tile-row'>
          {isAM ? <AmIcon /> : <PmIcon />}
          <Texto>{isAM ? 'AM Shift' : 'PM Shift'}</Texto>
          {showEdit && <EditFilled style={{ color: 'var(--gray-500)' }} />}
        </div>
        <StartHourDisplay value={value.shift_start_hour_local} />
      </GraviButton>
    </Popover>
  )
}
